/* istanbul ignore file */
// this is a mock file for testing

import { DEFAULT_LOGO_URL } from '@seek/libs-shared';

import type { AppConfig } from '../../config';

import type { CompanyDetailsService } from './companyDetailsService';
import type { CompanyDetails } from './types';

export const createCompanyDetailsServiceMock = (_props: {
  config: AppConfig;
}): CompanyDetailsService => {
  const getCompanyDetails = async (
    companyId: string,
    isDraft = false,
  ): Promise<CompanyDetails> => {
    const companyDetails: CompanyDetails = {
      id: companyId,
      companyReviewLocations: null,
      companyProfile: {
        organisationId: '123456789',
        zone: 'anz-1',
        data: {
          name: `SEEK${isDraft ? ' (Draft)' : ''}`,
          canonicalUrl: `https://www.seek.com.au/company/${companyId}`,
          gallery: [
            {
              linkUrl:
                'https://image-service-cdn.staging.seek.com.au/73a9ac271ce110bfdc91562ba0619b37f4630a5f/42bf52e76925e2211c3d956332bf254f2157d3de',
              date: 675657675,
              title: 'Gallery Image 1',
            },
          ],
          hasFirstNationSignal: null,
          overview: {
            description: [
              "SEEK's portfolio of diverse businesses make a positive impact on a truly global scale. Our purpose is to help people live more fulfilling and productive working lives and help organisations succeed. We create world-class technology solutions to connect more people to relevant employment, education, small business and volunteer opportunities.",
              'We have a culture of high-performance in our workplaces and celebrate the diversity of our employees who contribute to the success of our organisation.',
              'Coffee',
              'Tea',
            ],
            industry: 'Advertising, Arts & Media',
            primaryLocation: 'Melbourne, Victoria',
            size: '101-5,000 employees',
            specialities: [
              'Online Employment Classifieds',
              'Job Boards',
              'Education',
            ],
            website: {
              url: 'https://www.seek.melbourne',
              displayName: 'seek.melbourne',
            },
            traditionalCountry: null,
          },
          awards: [
            {
              imageUrl:
                'https://image-service-cdn.staging.seek.com.au/e120d605b28c091d53f459357bf492afb07b9128',
              name: 'Best Places to work',
              year: 2021,
            },
            {
              imageUrl:
                'https://image-service-cdn.staging.seek.com.au/616f7e0d8c4ff2b3611a5229d7420a792b2a0562',
              name: 'Carbon Neutral Organisation',
              year: 2022,
            },
          ],
          branding: {
            brandId: 'a2345-2345-2345-2345',
            logo: DEFAULT_LOGO_URL,
            cover:
              'https://image-service-cdn.staging.seek.com.au/73a9ac271ce110bfdc91562ba0619b37f4630a5f/42bf52e76925e2211c3d956332bf254f2157d3de',
          },
          cultureAndValues: {
            values: [
              {
                title: 'We always... Seek better',
                description:
                  "We don't settle for 'good'. Innately curious and creative, we actively look for new and better ways to do things that deliver improved customer outcomes.",
              },
              {
                title: 'We always... Bite the bullet',
                description:
                  "We do what's right, not what's easy. Brave but considered, we don't shy away from doing the hard stuff if we believe. Bring about better outcomes for our customers or our business.",
              },
              {
                title: 'We always... Think for tomorrow',
                description:
                  'We never rest on our laurels. Relentlessly forward-thinking and strategic, we play the long game without losing sight of the here and now.',
              },
              {
                title: 'We always... Share the good stuff',
                description:
                  'We share so others grow. Always ready and willing to help, we share our time, knowledge and experience to make work better for people, companies and each other.',
              },
            ],
            title: 'Our beliefs',
            imageUrl:
              'https://image-service-cdn.staging.seek.com.au/544e754c8fb297ec4b74024a01a086706528417d',
            description:
              "The beliefs are SEEK's secret recipe to success. They state the way we do things at SEEK and are based around the fundamental things the organisation was built on.",
          },
          customStatement: {
            title: 'Our approach to diversity, equity, and inclusion',
            description: [
              'We recognise and respect qualities which are unique to individuals such as gender, language, ethnicity, age, religion, disability and sexual orientation. We are recognised as having an approach to diversity and inclusion which is genuine and embedded within our culture.',
            ],
          },
          featured: {
            title: 'Best places 2021 awards',
            videoUrl: null,
            imageUrl:
              'https://image-service-cdn.staging.seek.com.au/82c608d175d14bfb9e6d79d23de1600acdf17b52',
            description: [
              "Our people are what make SEEK the company it is today, and we're honoured to be recognised as the Best Place to Work Overall and the #1 Best Place to Work - Tech Industry in The Australian Financial Review #BestPlaces2021 Awards. We wouldn't be where we are today without the SEEKers who make this company what it is - thank you!",
            ],
          },
          missionStatement: [
            'We help people live more fulfilling and productive working lives and help organisations succeed.',
            'As a global airline, Singapore Airlines is dedicated to providing air transportation services of the highest quality, and to maximising returns for the benefit of our shareholders and employees.',
            "With a commitment to fleet modernisation, product and service innovation and market leadership, Singapore Airlines has evolved into one of the world's most respected travel brands.",
            "Today, Singapore Airlines is internationally recognised as the world's leading airline and operates a modern passenger fleet of more than 100 aircraft.  Its network covers more than 60 cities in more than 30 countries.",
          ],

          perksAndBenefits: [
            {
              title: 'Family focus',
              description:
                '14 weeks paid primary carer leave with Parental coaching program Flexible working',
            },
            {
              title: 'Diversity',
              description:
                'Initiatives to cultivate a diverse and inclusive environment',
            },
            {
              title: 'Learning & Development',
              description:
                'Supporting our people to reach their full potential',
            },
            {
              title: 'Flexible working',
              description:
                'Pick ups? Drop offs? Long commute? No worries! We work flexibly at SEEK',
            },
            {
              title: 'Free fuel',
              description: 'Free coffee and breakfast, everyday!',
            },
            {
              title: 'Community impact',
              description:
                'Paid volunteer opportunities with our charity partners',
            },
          ],
        },
        companyReviewRating: {
          company: {
            rating: 4.2083,
            percentage: 87.5,
            count: 24,
          },
          workLifeBalance: {
            rating: 4.2083,
            percentage: 84.166,
            count: 24,
          },
          careerOpportunity: {
            rating: 3.7917,
            percentage: 75.834,
            count: 24,
          },
          diversity: {
            rating: 4.2917,
            percentage: 85.834,
            count: 24,
          },
          companyRating1Star: {
            rating: 4.2083,
            percentage: 87.5,
            count: 24,
          },
          perksAndBenefits: {
            rating: 4.2083,
            percentage: 87.5,
            count: 24,
          },
          executiveManagement: {
            rating: 4.2083,
            percentage: 87.5,
            count: 24,
          },
          workEnvironment: {
            rating: 4.2083,
            percentage: 87.5,
            count: 24,
          },
          companyRating2Star: null,
          companyRating3Star: null,
          companyRating4Star: null,
          companyRating5Star: null,
          companyRecommended: null,
          salaryPoor: null,
          salaryFair: null,
          salaryGenerous: null,
        },
      },
      companyReviewsAISummary: {
        reviewsCount: 2,
        reviewsDateFrom: '2024-01-01T00:22:48.000Z',
        reviewsDateTo: '2024-02-01T00:22:48.000Z',
        aISummaryContent: {
          challengesTheme: null,
          positiveTheme: null,
          positiveAndChallengesSummary:
            '<p>Paragraph one, this is a <strong>hard-coded sample</strong> that acts as mock data for visualizing AI-generated summary content, it supports multiple lines, and all content is generated from AWS Bedrock.</p>Employees list several positives, including attractive benefits, staff discounts, work-from-home options, and a supportive team environment that promotes flexibility and work-life balance. Employees also appreciate opportunities for training and career development.<p/> However, challenges include concerns around management, with some citing micromanagement and shifting priorities, as well as outdated systems and processes. Additionally, high workloads and job pressures, especially in customer-facing roles, along with frequent organisational changes, can contribute to stress and job insecurity.',
        },
      },
    };

    return companyDetails;
  };

  return {
    getCompanyDetails,
  };
};
